const SIGNATURE_ACTIONS = Object.freeze(['released', 'agreed', 'both']);
const SIGNATURE_REASONS = Object.freeze([
	null,
	'requires_l2',
	'out_of_scope',
	'already_signed',
	'completed',
	'legacy_scope_required',
]);
const SCOPE_STATUSES = Object.freeze(['SCOPED', 'LEGACY_SCOPE_REQUIRED']);
const TRANSACTION_WORKFLOW_STATES = Object.freeze([
	'EDITABLE_DRAFT',
	'PENDING_L2',
	'COMPLETED',
	'LEGACY_SCOPE_REQUIRED',
]);

export const CAPABILITIES_FETCH_STATUS = Object.freeze({
	IDLE: 'idle',
	LOADING: 'loading',
	READY: 'ready',
	ERROR: 'error',
});

const failContract = message => {
	throw new Error(`Invalid transaction capabilities response: ${message}`);
};

const requireBoolean = (value, field) => {
	if (typeof value !== 'boolean') failContract(`${field} must be boolean`);
	return value;
};

const normalizeId = (value, field, { nullable = false } = {}) => {
	if (nullable && value == null) return null;
	const normalized = Number(value);
	if (!Number.isInteger(normalized) || normalized <= 0)
		failContract(`${field} must be a positive integer`);
	return normalized;
};

export const normalizeTransactionCapabilities = (
	response = {},
	expectedDocumentId,
) => {
	if (!response || typeof response !== 'object' || Array.isArray(response))
		failContract('payload must be an object');

	const documentId = normalizeId(response.documentId, 'documentId');
	const expectedId = normalizeId(expectedDocumentId, 'expectedDocumentId');
	if (documentId !== expectedId)
		failContract('documentId does not match request');
	if (!SCOPE_STATUSES.includes(response.scopeStatus))
		failContract('scopeStatus is unknown');
	const hasCanonicalWorkflowState = Object.prototype.hasOwnProperty.call(
		response,
		'workflowState',
	);
	const hasDeprecatedStateAlias = Object.prototype.hasOwnProperty.call(
		response,
		'state',
	);
	if (
		hasCanonicalWorkflowState &&
		hasDeprecatedStateAlias &&
		response.workflowState !== response.state
	)
		failContract('workflowState conflicts with deprecated state alias');
	// TODO(contract): remove this rolling DTO alias after the API/cache migration.
	// This alias is not the persisted warehouses_transaction_docs.state column.
	const workflowState = hasCanonicalWorkflowState
		? response.workflowState
		: response.state;
	if (!TRANSACTION_WORKFLOW_STATES.includes(workflowState))
		failContract('workflowState is unknown');

	const capabilities = response.capabilities;
	if (!capabilities || typeof capabilities !== 'object')
		failContract('capabilities must be an object');
	const signature = capabilities.signature;
	if (!signature || typeof signature !== 'object')
		failContract('capabilities.signature must be an object');
	if (!Array.isArray(signature.allowedActions))
		failContract('signature.allowedActions must be an array');
	if (
		signature.allowedActions.some(action => !SIGNATURE_ACTIONS.includes(action))
	)
		failContract('signature.allowedActions contains an unknown action');
	if (!SIGNATURE_REASONS.includes(signature.reason ?? null))
		failContract('signature.reason is unknown');

	return {
		documentId,
		orderTargetId: normalizeId(response.orderTargetId, 'orderTargetId', {
			nullable: true,
		}),
		scopeStatus: response.scopeStatus,
		workflowState,
		capabilities: {
			// During rolling deployment, an older API cannot authorize this mode.
			canConfirmPhysicalReturn: capabilities.canConfirmPhysicalReturn === undefined
				? false
				: requireBoolean(capabilities.canConfirmPhysicalReturn, 'capabilities.canConfirmPhysicalReturn'),
			canEditDraft: requireBoolean(
				capabilities.canEditDraft,
				'capabilities.canEditDraft',
			),
			canFinalize: requireBoolean(
				capabilities.canFinalize,
				'capabilities.canFinalize',
			),
			canDelete: requireBoolean(
				capabilities.canDelete,
				'capabilities.canDelete',
			),
			signature: {
				allowedActions: [...new Set(signature.allowedActions)],
				pending: requireBoolean(
					signature.pending,
					'capabilities.signature.pending',
				),
				reason: signature.reason ?? null,
			},
		},
	};
};

const getPreferredSignatureAction = allowedActions => {
	if (allowedActions.includes('both')) return 'both';
	if (allowedActions.length !== 1) return undefined;
	return allowedActions[0];
};

const getSignatureLabel = action =>
	({
		released: 'Autorizar envío',
		agreed: 'Autorizar recepción',
		both: 'Autorizar y completar',
	})[action];

const denyDraftAwareActions = (flowActions, capabilitiesState = {}) => ({
	...flowActions,
	canEditDraft: false,
	canFinalizeDraft: false,
	canDeleteDocument: false,
	canRelease: false,
	canAgree: false,
	canSign: false,
	signatureAction: undefined,
	signatureLabel: undefined,
	authorizationPending: false,
	authorizationReason: undefined,
	authorizationScopeStatus: undefined,
	authorizationWorkflowState: undefined,
	capabilitiesFetchStatus:
		capabilitiesState.status || CAPABILITIES_FETCH_STATUS.IDLE,
});

export const resolveTransactionFlowActionsWithCapabilities = (
	flowActions,
	capabilitiesState = {},
	documentId,
) => {
	if (!flowActions?.usesDraftFlow) return flowActions;

	const denied = denyDraftAwareActions(flowActions, capabilitiesState);
	const normalizedDocumentId = Number(documentId);
	if (
		capabilitiesState.status !== CAPABILITIES_FETCH_STATUS.READY ||
		!capabilitiesState.data ||
		Number(capabilitiesState.documentId) !== normalizedDocumentId ||
		Number(capabilitiesState.data.documentId) !== normalizedDocumentId
	)
		return denied;

	const authorization = capabilitiesState.data;
	const { capabilities } = authorization;
	const allowedActions = capabilities.signature.allowedActions;
	const signatureAction = getPreferredSignatureAction(allowedActions);

	return {
		...denied,
		canEditDraft: capabilities.canEditDraft,
		canFinalizeDraft: capabilities.canFinalize,
		canDeleteDocument: capabilities.canDelete,
		canRelease:
			allowedActions.includes('released') || allowedActions.includes('both'),
		canAgree:
			allowedActions.includes('agreed') || allowedActions.includes('both'),
		canSign: Boolean(signatureAction),
		signatureAction,
		signatureLabel: getSignatureLabel(signatureAction),
		authorizationPending: capabilities.signature.pending,
		authorizationReason: capabilities.signature.reason,
		authorizationScopeStatus: authorization.scopeStatus,
		authorizationWorkflowState: authorization.workflowState,
		capabilitiesFetchStatus: CAPABILITIES_FETCH_STATUS.READY,
	};
};

export const getTransactionAuthorizationStatusText = flowActions => {
	if (!flowActions?.usesDraftFlow) return undefined;
	if (flowActions.capabilitiesFetchStatus === CAPABILITIES_FETCH_STATUS.LOADING)
		return 'Verificando autorización';
	if (flowActions.capabilitiesFetchStatus === CAPABILITIES_FETCH_STATUS.ERROR)
		return 'No fue posible verificar la autorización';
	if (
		flowActions.authorizationReason === 'legacy_scope_required' ||
		flowActions.authorizationScopeStatus === 'LEGACY_SCOPE_REQUIRED'
	)
		return 'Autorización pendiente: la transacción no tiene un alcance asignado';
	if (
		flowActions.authorizationPending &&
		['requires_l2', 'out_of_scope'].includes(flowActions.authorizationReason)
	)
		return 'Pendiente de autorización L2';
	return undefined;
};

export const getTransactionPrimaryActionLabel = ({
	showFinalizeAction,
	reviewMode,
	flowActions = {},
} = {}) => {
	if (!showFinalizeAction) return flowActions.signatureLabel;
	if (!reviewMode) return 'Finalizar';
	return flowActions.isNewInventory ? 'Confirmar ingreso' : 'Confirmar';
};

export const runTransactionPrimaryAction = ({
	disabled = false,
	showSignatureAction = false,
	reviewMode = false,
	onSign,
	onReview,
	onConfirm,
} = {}) => {
	if (disabled) return 'blocked';
	if (showSignatureAction) {
		onSign?.();
		return 'sign';
	}
	if (reviewMode) {
		onConfirm?.();
		return 'confirm';
	}
	onReview?.();
	return 'review';
};

export const runTransactionMutationWithCapabilitiesRefresh = ({
	mutate,
	onMutated,
	refreshCapabilities,
	shouldApply = () => true,
}) =>
	Promise.resolve()
		.then(() => mutate())
		.then(result => {
			if (!shouldApply()) return result;
			onMutated?.(result);
			return Promise.resolve(refreshCapabilities()).then(() => result);
		});

export const runTransactionDocumentRequest = ({
	request,
	shouldAccept = () => true,
	onAccepted,
	onRejected,
}) =>
	Promise.resolve()
		.then(() => request())
		.then(result => {
			if (!shouldAccept()) return null;
			const acceptedResult = onAccepted?.(result);
			return acceptedResult === undefined ? result : acceptedResult;
		})
		.catch(error => {
			if (!shouldAccept()) return null;
			if (onRejected) return onRejected(error);
			throw error;
		});

export const shouldAcceptTransactionCapabilitiesResponse = ({
	requestId,
	activeRequestId,
	documentId,
	activeDocumentId,
}) =>
	requestId === activeRequestId &&
	Number(documentId) === Number(activeDocumentId);
